import { ReadonlySignal, signal, Signal } from '@preact/signals-react';
import { injectable } from 'inversify';

import { SplashService } from '@vp/common/ui/SplashService';
import { ViewModel, ViewModelDispose, ViewModelInit } from '@vp/common/ui/ViewModel';
import { sleep } from '@vp/common/util';
import { LoadProfileDto } from '@vp/profile/core/dto/LoadProfileDto';
import { ProfilePort } from '@vp/profile/core/interface/ProfilePort';
import { ProfileLoadingState } from '@vp/profile/core/interface/ProfileState';
import { ProfileModel } from '@vp/profile/core/model/ProfileModel';
import { RouterService } from '@vp/routing/RouterService';

@injectable()
export class ProfileGatewayViewModel extends ViewModel implements ViewModelInit, ViewModelDispose {
  readonly state: ReadonlySignal<ProfileLoadingState> = this.profilePort.loadingState;
  readonly profile: ReadonlySignal<ProfileModel> = this.profilePort.profile;
  readonly showRestriction: Signal<boolean> = signal(false);
  readonly isUnlocked: Signal<boolean> = signal(false);

  private controller?: AbortController;
  private clearEffect?: () => void;

  constructor(
    private readonly profilePort: ProfilePort,
    private readonly router: RouterService,
    private readonly splashService: SplashService,
  ) {
    super();
  }

  async init(): Promise<void> {
    this.splashService.show();

    this.resetState();
    const dto = this.toDto();
    await this.profilePort.loadProfile(dto);
    await this.router.clearQueryParam('code');
    this.showRestrictionIfNeeded();

    await this.splashService.hide();
  }

  dispose(): void {
    this.controller?.abort();
    this.clearEffect?.();
  }

  onProfileUnlock = async (profile: ProfileModel): Promise<void> => {
    await this.profilePort.setProfile(profile);
    await this.profilePort.scheduleSlideshow();

    this.isUnlocked.value = true;
    await sleep(1000);
    this.showRestriction.value = false;
  };

  private resetState(): void {
    this.showRestriction.value = false;
    this.isUnlocked.value = false;
    this.controller = new AbortController();
  }

  private toDto(): LoadProfileDto {
    const { id } = this.router.getParams('p/:id/*');
    const code = this.router.getQueryParam('code');
    return { id, code, controller: this.controller };
  }

  private showRestrictionIfNeeded(): void {
    if (this.profile.value.access.isFullyRestricted()) {
      this.showRestriction.value = true;
    }
  }
}
